import React from 'react';
import { Form, Select, Input } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { FileOutlined } from '@ant-design/icons';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { truncateFileName } from '@hooks/projects/projectHelpers';
import { contentToApiMentions } from '@utils/richTextMentions';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';

export default function IssueUpgradeSubmitModal({ open, onCancel, onSubmit, loading = false }) {
    const [form] = Form.useForm();
    const selectedState = Form.useWatch('state', form) ?? 'genuine';
    const isImproper = selectedState === 'improper';

    const [pendingFiles, setPendingFiles] = React.useState([]);
    const [pendingLinks, setPendingLinks] = React.useState([]);
    const [linkModalOpen, setLinkModalOpen] = React.useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) {
            form.resetFields();
            setPendingFiles([]);
            setPendingLinks([]);
            return;
        }
        form.setFieldsValue({ state: 'genuine', reason: '' });
    }, [open, form]);

    React.useEffect(() => {
        return () => {
            pendingFiles.forEach((p) => {
                if (p?.preview && p.preview.startsWith('blob:')) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {}
                }
            });
        };
    }, [pendingFiles]);

    const handleFilesSelected = React.useCallback((files) => {
        if (!files?.length) return;
        const next = Array.from(files).map((file, i) => ({
            id: `submit-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const removePendingFile = React.useCallback((id) => {
        setPendingFiles((prev) => {
            const index = prev.findIndex((p) => p.id === id);
            if (index < 0) return prev.filter((p) => p.id !== id);
            let content = form.getFieldValue('reason') || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            for (let j = prev.length - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            form.setFieldValue('reason', content);
            return prev.filter((p) => p.id !== id);
        });
    }, [form]);

    const handleLinkRequest = React.useCallback((insertLink) => {
        linkModalCallbackRef.current = insertLink;
        linkModalForm.resetFields();
        setLinkModalOpen(true);
    }, [linkModalForm]);

    const handleLinkModalSubmit = React.useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = String(values?.url ?? '').trim();
            const title = String(values?.title ?? '').trim();
            if (!url) return;
            const insertLink = linkModalCallbackRef.current;
            if (typeof insertLink === 'function') {
                insertLink({ url, title: title || url });
            }
            setPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const removePendingLink = React.useCallback((id) => {
        setPendingLinks((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const isImproperState = values?.state === 'improper';
            let reasonText = '';
            
            if (isImproperState) {
                reasonText = contentToApiMentions(values?.reason ?? '');
            } else {
                reasonText = String(values?.reason ?? '').trim();
            }

            const payload = {
                state: isImproperState ? 'improper' : 'genuine',
                reason: reasonText,
                files: isImproperState ? pendingFiles.map((p) => p.file) : [],
                attachments: isImproperState ? pendingLinks.map(({ url, title }) => ({ type: 'link', url, title })) : [],
            };
            onSubmit?.(payload);
        });
    };

    return (
        <>
        <AppModal
            size="md"
            title={<ModalChromeHeading>Submit issue or upgrade</ModalChromeHeading>}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button $primary={false} $size="14px" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={loading}
                        icon={<SendOutlined />}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    initialValues={{ state: 'genuine', reason: '' }}
                >
                    <Form.Item
                        name="state"
                        label={<ModalBlockLabel>Type</ModalBlockLabel>}
                        rules={[{ required: true, message: 'Select type' }]}
                    >
                        <Select
                            options={[
                                { label: 'Genuine', value: 'genuine' },
                                { label: 'Improper', value: 'improper' },
                            ]}
                        />
                    </Form.Item>
                    {isImproper ? (
                        <Form.Item
                            name="reason"
                            label={<ModalBlockLabel>Reason (Markdown)</ModalBlockLabel>}
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        const plain = String(value ?? '')
                                            .replace(/<[^>]*>/g, ' ')
                                            .replace(/&nbsp;/gi, ' ')
                                            .trim();
                                        if (plain) return;
                                        throw new Error('Enter reason for improper item');
                                    },
                                },
                            ]}
                        >
                            <RichTextEditor
                                placeholder="Write markdown reason. Example: Duplicate item, out of scope, or missing details."
                                showAttachButton
                                attachmentIndexOffset={pendingFiles.length}
                                onFilesSelected={handleFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onAttachmentRemoved={(index) => {
                                    const p = pendingFiles[index];
                                    if (p) removePendingFile(p.id);
                                }}
                            />
                        </Form.Item>
                    ) : null}

                    {isImproper && pendingLinks.length > 0 && open && (
                        <PendingLinkWrap links={pendingLinks} onRemoveLink={removePendingLink} />
                    )}

                    {isImproper && pendingFiles.length > 0 && (
                        <div style={{ marginTop: 8, marginBottom: 16 }}>
                            <PanelSectionLabel
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    opacity: 0.75,
                                }}
                            >
                                <FileOutlined />
                                Attachments
                            </PanelSectionLabel>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                    gap: 10,
                                }}
                            >
                                {pendingFiles.map((p) => (
                                    <PendingAttachmentTile
                                        key={p.id}
                                        item={p}
                                        onRemove={() => removePendingFile(p.id)}
                                        truncateFileName={truncateFileName}
                                        showBottomFileName={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </Form>
            </CreateRoleModalBody>
        </AppModal>
        
        <AppModal
            size="sm"
            title={<ModalChromeHeading>Add link</ModalChromeHeading>}
            open={linkModalOpen}
            onCancel={() => {
                setLinkModalOpen(false);
                linkModalCallbackRef.current = null;
            }}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={() => {
                            setLinkModalOpen(false);
                            linkModalCallbackRef.current = null;
                        }}
                    >
                        Cancel
                    </Button>
                    <Button $primary $blackOnLight $size="14px" icon={<SendOutlined />} onClick={handleLinkModalSubmit}>
                        Add link
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <Form form={linkModalForm} layout="vertical" requiredMark={false}>
                    <Form.Item name="url" label={<ModalBlockLabel>URL</ModalBlockLabel>} rules={[{ required: true }]}>
                        <Input placeholder="https://..." size="large" />
                    </Form.Item>
                    <Form.Item name="title" label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                        <Input placeholder="Optional display text" size="large" />
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
        </AppModal>
        </>
    );
}

